import { useEffect } from 'react';
import { CAPABILITIES } from '@/lib/types/authorization';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { useConfigStore } from '@/lib/config';
import { useTokenRefresh } from '@/lib/hooks/useTokenRefresh';
import { useScale } from '@/lib/hooks/useScale';
import { useAuthStore } from '@/lib/stores/authStore';
import { fetchProjectConfig, fetchOrganizations } from '@/lib/api/auth';
import { DepartmentThemeProvider } from '@/components/providers/DepartmentThemeProvider';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { RecordsPage } from '@/pages/RecordsPage';
import { InvestigationOrdersPage } from '@/pages/InvestigationOrdersPage';
import { PatientFormPage } from '@/pages/PatientFormPage';
import { PatientRecordPage } from '@/pages/PatientRecordPage';
import { HospitalDashboardPage } from '@/pages/HospitalDashboardPage';
import { PractitionerPage } from '@/pages/PractitionerPage';
import { PractitionersPage } from '@/pages/PractitionersPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { DoctorProfilePage } from '@/pages/DoctorProfilePage';
import { ProfilePage } from '@/pages/ProfilePage';
import { PatientProfilePage } from '@/pages/PatientProfilePage';
import { AppointmentManagementPage } from '@/pages/AppointmentManagementPage';
import ConsultationPage from '@/pages/ConsultationPage';
import CaseSheetPage from '@/pages/CaseSheetPage';
import { ProtectedRoute } from '@/components/shared/ProtectedRoute';
import { Spinner } from '@/components/base/Spinner';
import { Body } from '@/components/base/Typography';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (garbage collection time)
    },
  },
});

function App() {
  const { loadConfig, isLoaded, isLoading } = useConfigStore();
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated());

  // Initialize global scaling system for resolution-independent UI
  useScale();

  // Enable proactive token refresh to prevent session expiration
  useTokenRefresh();

  // Validate session on every page refresh
  // This checks if the token exists and is not expired
  useEffect(() => {
    if (isInitialized) {
      const { validateSession, clearAuth } = useAuthStore.getState();

      // If session is invalid (no token, expired token, or no user), clear auth
      if (!validateSession()) {
        clearAuth();
      }
    }
  }, [isInitialized]);

  // If user has a token from a previous session but projectConfig wasn't persisted,
  // fetch it from the backend so feature flags are applied immediately on refresh.
  useEffect(() => {
    const { token, projectConfig, setProjectConfig } = useAuthStore.getState();
    if (token && !projectConfig) {
      fetchProjectConfig().then((config) => {
        if (config) setProjectConfig(config);
      });
    }
  }, []);

  useEffect(() => {
    if (isInitialized && isAuthenticated) {
      // Sync user configuration from backend after page refresh
      // This ensures the frontend has the latest menu and role
      // Only runs if session is valid (token exists and not expired)
      const { syncUserConfiguration, clearAuth } = useAuthStore.getState();

      syncUserConfiguration().then((success) => {
        // If sync failed and returned false, it might be due to invalid token
        // The syncUserConfiguration already validates the session, so if it returns false,
        // we should clear auth
        if (!success) {
          // Check if it's because the session is invalid
          const { validateSession } = useAuthStore.getState();
          if (!validateSession()) {
            clearAuth();
          }
        }
      });

      // Fetch organizations on page reload when user is authenticated
      // This ensures the organization list is available for the header dropdown
      const { setOrganizations, setSelectedOrganization } = useAuthStore.getState();
      fetchOrganizations().then((orgs) => {
        if (orgs.length > 0) {
          setOrganizations(orgs);

          // Auto-select first organization if none is selected
          const { selectedOrganization } = useAuthStore.getState();
          if (!selectedOrganization) {
            setSelectedOrganization(orgs[0]);
          }
        }
      });
    }
  }, [isInitialized, isAuthenticated]);

  // Load configuration on app initialization
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Show loading state while configuration loads
  if (isLoading || !isLoaded) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background"
        role="status"
        aria-live="polite"
        aria-label="Loading application"
      >
        <div className="text-center">
          <Spinner size="lg" />
          <Body className="mt-4 text-muted-foreground">Loading CDSS...</Body>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <DepartmentThemeProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hospital-dashboard"
              element={
                <ProtectedRoute requiredRole="admin">
                  <HospitalDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/practitioners"
              element={
                <ProtectedRoute>
                  <PractitionersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute requiredRole="admin">
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/records"
              element={
                <ProtectedRoute>
                  <RecordsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/investigation-orders"
              element={
                <ProtectedRoute>
                  <InvestigationOrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patient-form"
              element={
                <ProtectedRoute>
                  <PatientFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patient-form/:patientId"
              element={
                <ProtectedRoute>
                  <PatientFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patient-record/:patientId"
              element={
                <ProtectedRoute>
                  <PatientRecordPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/practitioner"
              element={
                <ProtectedRoute>
                  <DoctorProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/practitioner/:practitionerId"
              element={
                <ProtectedRoute>
                  <PractitionerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/consultation/:consultationId"
              element={
                <ProtectedRoute requiredCapability={CAPABILITIES.CONSULTATION_START}>
                  <ConsultationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/case-sheet/:consultationId"
              element={
                <ProtectedRoute requiredCapability={CAPABILITIES.CONSULTATION_START}>
                  <CaseSheetPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/appointments"
              element={
                <ProtectedRoute>
                  <AppointmentManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-profile"
              element={
                <ProtectedRoute>
                  <PatientProfilePage />
                </ProtectedRoute>
              }
            />

            {/* Redirect root to dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Catch-all redirect to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          <Toaster
            position="bottom-right"
            expand={true}
            richColors
            toastOptions={{ style: { zIndex: 9999 } }}
          />
        </BrowserRouter>
      </DepartmentThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
